import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  RefreshCw, CalendarDays, TrendingUp, ChevronRight,
  Info, CalendarPlus, Filter, X, CalendarClock, Check,
} from 'lucide-react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { useLeads } from '../context/LeadsContext';
import { useTasks } from '../context/TasksContext';
import { useCompany } from '../context/CompanyContext';
import {
  buildForecast,
  isoWeekKey,
  weeklyWorkloadSummary,
  HORIZON_OPTIONS,
  findEarliestLiveRecurringTask,
} from '../utils/buildForecast';
import { resolveServicePlanStep } from '../utils/resolveServicePlanStep';
import BookVisitModal from '../components/BookVisitModal';
import DateNavigator from '../components/DateNavigator';
import { useDateUrl } from '../hooks/useDateUrl';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Local date helpers ─────────────────────────────────────────────────────────

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function offsetDateStr(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ── String ⇄ Date helpers for the forecast filter (DateNavigator uses Date) ──

const ymdToDate = (ymd) => {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  const [y, m, d] = ymd.split('-').map(Number);
  const out = new Date(y, m - 1, d, 0, 0, 0, 0);
  return Number.isNaN(out.getTime()) ? null : out;
};

const dateToYmd = (date) => {
  if (!date || Number.isNaN(date.getTime?.())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatYmdLabel = (ymd) => {
  const d = ymdToDate(ymd);
  if (!d) return ymd || '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

// ── Label helpers ──────────────────────────────────────────────────────────────

function visitLabel(visit) {
  if (!visit.totalOccurrences) return `Visit #${visit.visitIndex} · ongoing`;
  return `Visit ${visit.visitIndex} of ${visit.totalOccurrences}`;
}

function intervalLabel(days) {
  if (days === 14) return 'Bi-Weekly';
  if (days === 30) return 'Monthly';
  if (days === 60) return 'Every 60 days';
  if (days === 90) return 'Quarterly';
  return `Every ${days} days`;
}

function statusLabel(status) {
  const map = { approved: 'Ready', en_route: 'En Route', on_site: 'On Site', scheduled: 'Scheduled' };
  return map[status] || 'Scheduled';
}

// ── Time bucket grouping (keeps buildForecast.js pure) ─────────────────────────

function groupByTimeBucket(projections) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  // ISO week bounds — Monday = 0
  const dow = (today.getDay() + 6) % 7;
  const thisMonday = new Date(today);
  thisMonday.setDate(today.getDate() - dow);
  const thisSunday = new Date(thisMonday);
  thisSunday.setDate(thisMonday.getDate() + 6);

  const nextMonday = new Date(thisMonday);
  nextMonday.setDate(thisMonday.getDate() + 7);
  const nextSunday = new Date(nextMonday);
  nextSunday.setDate(nextMonday.getDate() + 6);

  const getBucketKey = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    const t = d.getTime();
    if (t === today.getTime()) return 'today';
    if (t === tomorrow.getTime()) return 'tomorrow';
    if (d > tomorrow && d < nextMonday) return 'this_week';
    if (d >= nextMonday && d <= nextSunday) return 'next_week';
    return `later_${isoWeekKey(d)}`;
  };

  const getBucketLabel = (key) => {
    if (key === 'today') return 'Today';
    if (key === 'tomorrow') return 'Tomorrow';
    if (key === 'this_week') return 'This Week';
    if (key === 'next_week') return 'Next Week';
    // later_2026-W20 → human date range
    const weekKey = key.replace('later_', '');
    const [yearStr, wStr] = weekKey.split('-W');
    const year = parseInt(yearStr, 10);
    const week = parseInt(wStr, 10);
    const jan4 = new Date(year, 0, 4);
    const mondayW1 = new Date(jan4);
    mondayW1.setDate(jan4.getDate() - ((jan4.getDay() + 6) % 7));
    const monday = new Date(mondayW1);
    monday.setDate(mondayW1.getDate() + (week - 1) * 7);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return `${monday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${sunday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  };

  const bucketMap = new Map();
  const bucketOrder = [];

  for (const visit of projections) {
    const dateStr = visit.scheduledDate || visit.projectedDate;
    const key = getBucketKey(dateStr);
    if (!bucketMap.has(key)) {
      bucketMap.set(key, { key, label: getBucketLabel(key), visits: [] });
      bucketOrder.push(key);
    }
    bucketMap.get(key).visits.push(visit);
  }

  return bucketOrder.map((k) => bucketMap.get(k));
}

// ── Workload Summary widget (always based on all enriched, not filtered) ───────

function WorkloadSummary({ projections }) {
  const weeks = useMemo(() => weeklyWorkloadSummary(projections), [projections]);
  if (!weeks.length) return null;
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5" data-testid="workload-summary">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="h-4 w-4 text-gray-500" />
        <h2 className="text-sm font-semibold text-gray-900">Projected Workload by Week</h2>
      </div>
      <div className="space-y-2.5">
        {weeks.map((week) => (
          <div key={week.weekKey} className="flex items-center gap-3">
            <span className="text-xs text-gray-500 w-28 shrink-0">{week.weekLabel}</span>
            <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
              <div
                className="h-2 rounded-full bg-indigo-400 transition-all duration-300"
                style={{ width: `${week.barPct}%` }}
                data-testid={`workload-bar-${week.weekKey}`}
              />
            </div>
            <span className="text-xs font-medium text-gray-700 w-16 text-right shrink-0">
              {week.count} {week.count === 1 ? 'visit' : 'visits'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Forecast row ───────────────────────────────────────────────────────────────

function ForecastRow({ visit, lead, onBook, onReschedule }) {
  const isScheduled = visit.isScheduled === true;
  const effectiveDate = visit.scheduledDate || visit.projectedDate;

  const [showReschedule, setShowReschedule] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState(effectiveDate);
  const [saving, setSaving] = useState(false);

  const openReschedule = () => {
    setRescheduleDate(effectiveDate);
    setShowReschedule(true);
  };

  const handleApply = async () => {
    if (!rescheduleDate || rescheduleDate === effectiveDate) {
      setShowReschedule(false);
      return;
    }
    setSaving(true);
    try {
      await onReschedule(visit, rescheduleDate);
      setShowReschedule(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`
        rounded-lg overflow-hidden transition-all
        ${isScheduled
          ? 'border border-gray-200 opacity-70'
          : showReschedule
            ? 'border border-amber-400 shadow-md'
            : 'border border-gray-200 border-l-4 border-l-amber-400 shadow-sm hover:shadow-md'
        }
      `}
      data-testid={`forecast-row${isScheduled ? '-scheduled' : '-unbooked'}`}
    >
      {/* ── Main row ─────────────────────────────────────────────────── */}
      <div className={`flex items-center gap-3 px-4 py-3 group
        ${isScheduled ? 'bg-gray-50' : 'bg-white'}`}
      >
        {/* Date */}
        <div className="text-center w-16 shrink-0">
          <p className={`text-sm font-semibold ${isScheduled ? 'text-gray-500' : 'text-gray-900'}`}>
            {new Date(effectiveDate + 'T00:00:00').toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}
          </p>
          <p className={`text-xs ${isScheduled ? 'text-gray-400' : 'text-gray-500'}`}>
            {new Date(effectiveDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' })}
          </p>
        </div>

        {/* Divider */}
        <div className={`w-px h-8 shrink-0 ${isScheduled ? 'bg-gray-200' : 'bg-amber-200'}`} />

        {/* Plan info */}
        <div className="flex-1 min-w-0">
          <Link
            to={`/leads/${visit.leadId}`}
            className={`text-sm font-medium truncate block transition-colors ${
              isScheduled ? 'text-gray-500 hover:text-gray-700' : 'text-gray-900 hover:text-indigo-600'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {visit.clientName}
          </Link>
          <p className={`text-xs truncate ${isScheduled ? 'text-gray-400' : 'text-gray-500'}`}>
            {visit.programName ? visit.programName : visit.serviceLabel}
            {visit.servicePlanStep?.label ? ` · ${visit.servicePlanStep.label}` : ''}
            {!visit.programName && !visit.servicePlanStep?.label && (
              <span className="italic text-gray-400"> · No program linked</span>
            )}
          </p>
        </div>

        {/* Visit counter */}
        <div className="text-right shrink-0">
          <p className={`text-xs font-medium ${isScheduled ? 'text-gray-400' : 'text-gray-700'}`}>
            {visitLabel(visit)}
          </p>
          <p className="text-xs text-gray-400">{intervalLabel(visit.intervalDays)}</p>
        </div>

        {/* Status badge OR action buttons */}
        {isScheduled ? (
          <span
            data-testid="forecast-scheduled-badge"
            className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded border border-gray-300 bg-gray-100 text-gray-500 text-[10px] font-semibold uppercase tracking-wide"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-gray-400 inline-block" />
            {statusLabel(visit.scheduledStatus)}
          </span>
        ) : (
          <div className="shrink-0 flex items-center gap-1">
            <button
              type="button"
              onClick={() => onBook(visit, lead)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-50 border border-amber-300 text-amber-700 text-xs font-semibold hover:bg-amber-100 hover:border-amber-400 transition-colors"
              data-testid={`book-visit-btn-${visit.leadId}-${visit.visitIndex}`}
            >
              <CalendarPlus className="h-3.5 w-3.5" />
              Book
            </button>
            <button
              type="button"
              onClick={openReschedule}
              title="Reschedule visit"
              className={`p-1.5 rounded-md border transition-colors ${
                showReschedule
                  ? 'bg-amber-100 border-amber-400 text-amber-700'
                  : 'border-gray-200 text-gray-400 hover:border-gray-300 hover:text-gray-600'
              }`}
              data-testid={`reschedule-btn-${visit.leadId}-${visit.visitIndex}`}
            >
              <CalendarClock className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {/* Lead arrow */}
        <Link
          to={`/leads/${visit.leadId}`}
          className="shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <ChevronRight className={`h-4 w-4 transition-colors ${
            isScheduled ? 'text-gray-200' : 'text-gray-300 group-hover:text-gray-500'
          }`} />
        </Link>
      </div>

      {/* ── Inline reschedule form ────────────────────────────────────── */}
      {showReschedule && (
        <div
          className="bg-amber-50/80 border-t border-amber-200 px-4 py-3 flex flex-wrap items-center gap-3"
          data-testid={`reschedule-form-${visit.leadId}-${visit.visitIndex}`}
        >
          <CalendarClock className="h-4 w-4 text-amber-600 shrink-0" />
          <span className="text-xs font-semibold text-amber-800 shrink-0">Move to</span>
          <input
            type="date"
            value={rescheduleDate}
            min={todayStr()}
            onChange={(e) => setRescheduleDate(e.target.value)}
            className="text-xs border border-amber-300 rounded-lg px-2 py-1.5 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-amber-300"
            data-testid={`reschedule-date-input-${visit.leadId}-${visit.visitIndex}`}
          />
          <button
            type="button"
            onClick={handleApply}
            disabled={saving || !rescheduleDate}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-500 text-white text-xs font-semibold hover:bg-amber-600 disabled:opacity-50 transition-colors"
            data-testid={`reschedule-apply-btn-${visit.leadId}-${visit.visitIndex}`}
          >
            <Check className="h-3.5 w-3.5" />
            {saving ? 'Saving…' : 'Apply'}
          </button>
          <button
            type="button"
            onClick={() => setShowReschedule(false)}
            className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1.5 transition-colors"
            data-testid={`reschedule-cancel-btn-${visit.leadId}-${visit.visitIndex}`}
          >
            Cancel
          </button>
          <p className="text-[11px] text-amber-700 ml-auto opacity-80 hidden sm:block">
            This visit and future visits will shift to the new date.
          </p>
        </div>
      )}
    </div>
  );
}

// ── Bucket group ───────────────────────────────────────────────────────────────

function BucketGroup({ bucket, leadsById, onBook, onReschedule }) {
  const unbookedCount = bucket.visits.filter((v) => !v.isScheduled).length;
  const isNear = ['today', 'tomorrow', 'this_week'].includes(bucket.key);

  return (
    <div data-testid={`bucket-group-${bucket.key}`}>
      <div className="flex items-center gap-2 mb-2 px-1">
        <h3 className={`text-xs font-bold uppercase tracking-wider ${isNear ? 'text-gray-700' : 'text-gray-400'}`}>
          {bucket.label}
        </h3>
        {unbookedCount > 0 ? (
          <span
            className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold"
            data-testid={`bucket-unbooked-count-${bucket.key}`}
          >
            {unbookedCount} unbooked
          </span>
        ) : (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600 text-[10px] font-semibold">
            All scheduled
          </span>
        )}
      </div>
      <div className="space-y-1.5">
        {bucket.visits.map((visit, i) => (
          <ForecastRow
            key={`${visit.leadId}-${visit.visitIndex}-${i}`}
            visit={visit}
            lead={leadsById[visit.leadId]}
            onBook={onBook}
            onReschedule={onReschedule}
          />
        ))}
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function ForecastPage() {
  const { leads, updateLead } = useLeads();
  const { tasks }              = useTasks();
  const { companyId }          = useCompany();

  const [horizonDays, setHorizonDays]   = useState(60);
  const [programs, setPrograms]         = useState([]);
  const [bookingVisit, setBookingVisit] = useState(null);

  // ── Filter state — default: Unbooked · next 7 days ────────────────────────
  const [statusFilter,  setStatusFilter]  = useState('unbooked');
  const [programFilter, setProgramFilter] = useState('');
  const [dateFrom, setDateFrom]           = useState(todayStr);       // lazy init
  const [dateTo,   setDateTo]             = useState(() => offsetDateStr(7));

  // URL deep-link: ?date=YYYY-MM-DD seeds the "from" anchor and stays in
  // sync as the operator picks a new date. The horizon (dateTo) is derived
  // from horizonDays so we only need to sync one end.
  const { initialDate: urlDate, urlDateYmd, syncDate } = useDateUrl();
  useEffect(() => {
    // Honor the URL param on first mount AND on every Back/Forward.
    // Also bump dateTo forward by the default 7-day window so the
    // filter range stays valid after a deep-link jump.
    if (!urlDateYmd) return;
    if (urlDateYmd === dateFrom) return;
    setDateFrom(urlDateYmd);
    const from = ymdToDate(urlDateYmd);
    if (from) {
      const to = new Date(from);
      to.setDate(to.getDate() + 7);
      setDateTo(dateToYmd(to));
    }
  }, [urlDateYmd]);
  useEffect(() => { syncDate(ymdToDate(dateFrom)); }, [dateFrom, syncDate]);

  // ── Load programs once ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!companyId) return;
    const load = async () => {
      try {
        const token = await getAuth().currentUser?.getIdToken();
        const res = await axios.get(`${API}/api/service-programs/`, {
          params: { companyId },
          headers: { Authorization: `Bearer ${token}` },
        });
        setPrograms(res.data || []);
      } catch (err) {
        console.warn('[ForecastPage] Could not load programs:', err);
      }
    };
    load();
  }, [companyId]);

  // ── Indexed lookups ────────────────────────────────────────────────────────
  const leadsById = useMemo(() => {
    const map = {};
    for (const l of leads) map[l.id] = l;
    return map;
  }, [leads]);

  const programsById = useMemo(() => {
    const map = {};
    for (const p of programs) map[p.id] = p;
    return map;
  }, [programs]);

  // ── Raw projections (pure — no Firestore) ─────────────────────────────────
  const projections = useMemo(
    () => buildForecast(leads, tasks, horizonDays),
    [leads, tasks, horizonDays]
  );

  // ── Enrich with program + step context ────────────────────────────────────
  const enrichedProjections = useMemo(() => {
    return projections.map((proj) => {
      const lead = leadsById[proj.leadId];
      if (!lead?.servicePlanTemplateId) return proj;
      const program = programsById[lead.servicePlanTemplateId];
      if (!program) return proj;
      const step = resolveServicePlanStep(program, proj.visitIndex);
      return { ...proj, programName: program.name || null, servicePlanStep: step || null };
    });
  }, [projections, leadsById, programsById]);

  // ── Apply filters ──────────────────────────────────────────────────────────
  const filteredProjections = useMemo(() => {
    return enrichedProjections.filter((visit) => {
      // Status
      if (statusFilter === 'unbooked' && visit.isScheduled) return false;
      if (statusFilter === 'scheduled' && !visit.isScheduled) return false;

      // Program — graceful: rows with no linked program pass through on "All Programs"
      if (programFilter) {
        const lead = leadsById[visit.leadId];
        if (!lead?.servicePlanTemplateId || lead.servicePlanTemplateId !== programFilter) return false;
      }

      // Date range
      const dateStr = visit.scheduledDate || visit.projectedDate;
      if (dateFrom && dateStr < dateFrom) return false;
      if (dateTo   && dateStr > dateTo)   return false;

      return true;
    });
  }, [enrichedProjections, statusFilter, programFilter, dateFrom, dateTo, leadsById]);

  // ── Time bucket grouping ───────────────────────────────────────────────────
  const bucketGroups = useMemo(() => groupByTimeBucket(filteredProjections), [filteredProjections]);

  // ── Stats ──────────────────────────────────────────────────────────────────
  const activePlanCount   = useMemo(() => leads.filter((l) => l.recurrence?.active).length, [leads]);
  const scheduledCount    = useMemo(() => enrichedProjections.filter((p) => p.isScheduled).length, [enrichedProjections]);
  const projectedCount    = enrichedProjections.length - scheduledCount;
  const unbookedNeedCount = filteredProjections.filter((p) => !p.isScheduled).length;

  const handleBook = useCallback((visit, lead) => setBookingVisit({ visit, lead }), []);

  // Write the new anchor date directly so the projection chain shifts accordingly.
  // Unlike calendar drag (applyRollingRecurrence sets nextJobDate = date + interval),
  // here the operator is setting the projected visit date itself, so we write it as-is.
  //
  // S2 (stabilization): if a live (non-terminal) recurring task already exists
  // for this lead, the calendar is the source of truth — silently writing the
  // cursor here would put it out of sync with the booked job (Jason-style).
  // Bail out and instruct the operator to move the task on the calendar
  // instead, which uses the cursor-syncing reschedule path.
  const handleReschedule = useCallback(async (visit, newDateStr) => {
    const liveTask = findEarliestLiveRecurringTask(visit.leadId, tasks);
    if (liveTask) {
      toast.info(
        'This visit already has a job on the calendar.',
        {
          description:
            'Drag it to the new date on the Calendar — that keeps the plan ' +
            'cursor in sync. Editing the projected date here is only for ' +
            'visits not yet booked.',
        }
      );
      return;
    }
    try {
      await updateLead(visit.leadId, {
        'recurrence.nextJobDate': newDateStr,
      });
      // LeadsContext onSnapshot fires → buildForecast re-runs → rows update automatically
    } catch (err) {
      console.error('[ForecastPage] Reschedule failed:', err);
    }
  }, [updateLead, tasks]);

  const clearFilters = useCallback(() => {
    setStatusFilter('unbooked');
    setProgramFilter('');
    setDateFrom(todayStr());
    setDateTo(offsetDateStr(7));
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-5">

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-50 rounded-lg">
              <CalendarDays className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Recurring Workload Forecast</h1>
              <p className="text-sm text-gray-500">
                {activePlanCount} active {activePlanCount === 1 ? 'plan' : 'plans'} ·{' '}
                {scheduledCount > 0 ? (
                  <>
                    <span className="text-emerald-600 font-medium">{scheduledCount} scheduled</span>
                    {projectedCount > 0 && ` · ${projectedCount} unbooked`}
                  </>
                ) : (
                  <>{enrichedProjections.length} projected {enrichedProjections.length === 1 ? 'visit' : 'visits'}</>
                )}
              </p>
            </div>
          </div>

          {/* Horizon selector */}
          <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg p-1">
            {HORIZON_OPTIONS.map((opt) => (
              <button
                key={opt.days}
                type="button"
                onClick={() => setHorizonDays(opt.days)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  horizonDays === opt.days
                    ? 'bg-indigo-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                data-testid={`horizon-btn-${opt.days}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Filter bar ──────────────────────────────────────────────────── */}
        <div
          className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex flex-wrap items-center gap-3"
          data-testid="filter-bar"
        >
          <div className="flex items-center gap-1.5 text-gray-400 shrink-0">
            <Filter className="h-3.5 w-3.5" />
            <span className="text-xs font-semibold uppercase tracking-wide">Filter</span>
          </div>

          {/* Status toggle */}
          <div
            className="flex items-center gap-0.5 bg-gray-100 rounded-lg p-0.5"
            data-testid="status-filter"
          >
            {[
              { value: 'unbooked',  label: 'Unbooked' },
              { value: 'scheduled', label: 'Scheduled' },
              { value: 'all',       label: 'All' },
            ].map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setStatusFilter(opt.value)}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  statusFilter === opt.value
                    ? opt.value === 'unbooked'
                      ? 'bg-amber-500 text-white shadow-sm'
                      : opt.value === 'scheduled'
                      ? 'bg-emerald-500 text-white shadow-sm'
                      : 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid={`status-filter-${opt.value}`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Program dropdown */}
          <select
            value={programFilter}
            onChange={(e) => setProgramFilter(e.target.value)}
            className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-300"
            data-testid="program-filter"
          >
            <option value="">All Programs</option>
            {programs.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>

          {/* Date range — shared DateNavigator (parity with Calendar/Dispatch) */}
          <div className="flex items-center gap-1.5" data-testid="forecast-date-range">
            <DateNavigator
              value={ymdToDate(dateFrom) || new Date()}
              onChange={(d) => setDateFrom(dateToYmd(d))}
              label={formatYmdLabel(dateFrom)}
              testId="forecast-date-from-trigger"
              variant="pill"
              align="start"
            />
            <span className="text-gray-400 text-xs">→</span>
            <DateNavigator
              value={ymdToDate(dateTo) || new Date()}
              onChange={(d) => setDateTo(dateToYmd(d))}
              label={formatYmdLabel(dateTo)}
              testId="forecast-date-to-trigger"
              variant="pill"
              align="start"
            />
          </div>

          {/* Reset */}
          <button
            type="button"
            onClick={clearFilters}
            className="ml-auto flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
            data-testid="clear-filters-btn"
          >
            <X className="h-3 w-3" />
            Reset
          </button>
        </div>

        {/* Action prompt — only shown in Unbooked mode */}
        {statusFilter === 'unbooked' && unbookedNeedCount > 0 && (
          <p className="text-xs text-amber-600 font-medium px-1" data-testid="unbooked-action-prompt">
            {unbookedNeedCount} {unbookedNeedCount === 1 ? 'visit needs' : 'visits need'} booking in this window
          </p>
        )}

        {/* ── Disclaimer ──────────────────────────────────────────────────── */}
        <div
          className="flex items-start gap-2 bg-indigo-50 border border-indigo-100 rounded-lg px-4 py-3"
          data-testid="forecast-disclaimer"
        >
          <Info className="h-4 w-4 text-indigo-500 shrink-0 mt-0.5" />
          <p className="text-xs text-indigo-700">
            <strong>Projected dates.</strong> Estimated future visits from active service plans.
            Click <strong>Book</strong> on any row to create a scheduled job.
            Rows marked <strong>Scheduled</strong> already have a job on the calendar.
          </p>
        </div>

        {/* ── Empty states ─────────────────────────────────────────────────── */}
        {activePlanCount === 0 && (
          <div className="text-center py-16 bg-white rounded-lg border border-gray-200" data-testid="forecast-empty">
            <RefreshCw className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-700">No active service plans</p>
            <p className="text-xs text-gray-400 mt-1">
              Activate a recurring service plan on a lead to see projected visits here.
            </p>
          </div>
        )}
        {activePlanCount > 0 && filteredProjections.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200" data-testid="forecast-empty-horizon">
            <CalendarDays className="h-8 w-8 text-gray-200 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-700">
              {enrichedProjections.length > 0 ? 'No visits match these filters' : 'No projected visits in this window'}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              {enrichedProjections.length > 0
                ? 'Try adjusting filters or extending the horizon.'
                : 'Try extending the horizon.'}
            </p>
          </div>
        )}

        {/* ── Forecast list ────────────────────────────────────────────────── */}
        {/* Workload summary always shows full horizon for context, even when filters narrow the list */}
        {enrichedProjections.length > 0 && (
          <WorkloadSummary projections={enrichedProjections} />
        )}

        {filteredProjections.length > 0 && (
          <div className="space-y-7" data-testid="forecast-list">
            {bucketGroups.map((bucket) => (
              <BucketGroup
                key={bucket.key}
                bucket={bucket}
                leadsById={leadsById}
                onBook={handleBook}
                onReschedule={handleReschedule}
              />
            ))}
          </div>
        )}
      </div>

      {/* Booking modal */}
      {bookingVisit && (
        <BookVisitModal
          visit={bookingVisit.visit}
          lead={bookingVisit.lead}
          companyId={companyId}
          onClose={() => setBookingVisit(null)}
          onBooked={() => setBookingVisit(null)}
        />
      )}
    </div>
  );
}
